/**
 * Monatskalender zum Auswählen eines Tages.
 *
 * Selbst gebaut und nicht `input type="date"`. Der Grund ist nachgemessen:
 * das native Feld stellt das Datum in der Sprache des **Browsers** dar,
 * nicht der Anwendung. Im Test stand dort „09/10/2026" für den 10.
 * September – amerikanisches Format in einer deutschen Anwendung. Wer
 * seinen Browser auf Englisch hat, sieht das auch im Betrieb, und bei einer
 * Prüffrist ist die Verwechslung von Tag und Monat kein Schönheitsfehler.
 *
 * Dazu kommt der Wochenanfang: der native Kalender beginnt je nach Sprache
 * am Sonntag. Hier fängt die Woche am Montag an.
 *
 * Kein Paket dafür. Ein Monatsraster ist Datumsarithmetik und sonst nichts,
 * und die Zeitzonenfalle darin ist mit der Mittagsstunde erledigt.
 */

import { useMemo, useState } from 'react'

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]

/** Mittags, nie um Mitternacht: sonst kippt die Zeitzone das Datum um
 *  einen Tag, und aus dem 1. März wird der 28. Februar. */
export function alsDatum(iso: string): Date {
  const [jahr, monat, tag] = iso.slice(0, 10).split('-').map(Number)
  return new Date(jahr, monat - 1, tag, 12)
}

export function alsIso(d: Date): string {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-')
}

/** „15.04.2027" → „2027-04-15". Leer, wenn es kein gültiges Datum ist. */
export function ausDeutsch(text: string): string {
  const treffer = text
    .trim()
    .match(/^(\d{1,2})[.\-/ ](\d{1,2})[.\-/ ](\d{2}|\d{4})$/)
  if (!treffer) return ''
  const [, t, m, j] = treffer
  const jahr = j.length === 2 ? 2000 + Number(j) : Number(j)
  const monat = Number(m)
  const tag = Number(t)
  if (monat < 1 || monat > 12 || tag < 1 || tag > 31) return ''
  const d = new Date(jahr, monat - 1, tag, 12)
  // Der 31. Februar rutscht in den März – dann war die Eingabe falsch.
  if (d.getMonth() !== monat - 1 || d.getDate() !== tag) return ''
  return alsIso(d)
}

export function nachDeutsch(iso: string): string {
  if (!iso) return ''
  const [jahr, monat, tag] = iso.slice(0, 10).split('-')
  return `${tag}.${monat}.${jahr}`
}

export function Kalender({
  wert,
  beimWaehlen,
}: {
  /** Der gewählte Tag als ISO, oder leer. */
  wert: string
  beimWaehlen: (iso: string) => void
}) {
  const [monatAnker, setzeMonatAnker] = useState(() =>
    wert ? alsDatum(wert) : new Date(),
  )
  const heute = alsIso(new Date())

  const tage = useMemo(() => {
    const jahr = monatAnker.getFullYear()
    const monat = monatAnker.getMonth()
    const erster = new Date(jahr, monat, 1, 12)
    // getDay(): 0 ist Sonntag. Auf Montag als Wochenanfang umgerechnet.
    const vorlauf = (erster.getDay() + 6) % 7
    const imMonat = new Date(jahr, monat + 1, 0, 12).getDate()

    const felder: (string | null)[] = Array(vorlauf).fill(null)
    for (let t = 1; t <= imMonat; t++) {
      felder.push(alsIso(new Date(jahr, monat, t, 12)))
    }
    // Auf ganze Wochen auffüllen, damit das Raster nicht springt, wenn man
    // den Monat wechselt.
    while (felder.length % 7 !== 0) felder.push(null)
    return felder
  }, [monatAnker])

  function monatVerschieben(um: number) {
    const d = new Date(monatAnker)
    d.setDate(1)
    d.setMonth(d.getMonth() + um)
    setzeMonatAnker(d)
  }

  return (
    <div className="rounded-feld border-2 border-slate-200 p-2">
      <div className="mb-2 flex items-center gap-2">
        <button
          type="button"
          className="knopf-still px-3"
          aria-label="Vorheriger Monat"
          onClick={() => monatVerschieben(-1)}
        >
          ‹
        </button>
        <span aria-live="polite" className="flex-1 text-center font-bold">
          {MONATE[monatAnker.getMonth()]} {monatAnker.getFullYear()}
        </span>
        <button
          type="button"
          className="knopf-still px-3"
          aria-label="Nächster Monat"
          onClick={() => monatVerschieben(1)}
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1" role="grid">
        {WOCHENTAGE.map((t) => (
          <div
            key={t}
            role="columnheader"
            className="py-1 text-center text-sm font-semibold text-slate-600"
          >
            {t}
          </div>
        ))}
        {tage.map((iso, i) =>
          iso === null ? (
            <div key={`leer-${i}`} />
          ) : (
            <button
              key={iso}
              type="button"
              role="gridcell"
              aria-selected={iso === wert}
              aria-current={iso === heute ? 'date' : undefined}
              onClick={() => beimWaehlen(iso)}
              className={
                'rounded-feld border-2 py-2 text-center tabular-nums ' +
                (iso === wert
                  ? 'border-akzent-500 bg-akzent-500 font-bold text-marke-900'
                  : iso === heute
                    ? 'border-marke-600 bg-white font-semibold hover:bg-marke-50'
                    : 'border-transparent bg-white hover:bg-slate-100')
              }
              style={{ minHeight: '44px' }}
            >
              {Number(iso.slice(8, 10))}
            </button>
          ),
        )}
      </div>
    </div>
  )
}
